import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, finalize, map } from 'rxjs';
import { BaseHttpService } from '@data-access/services/base-http.service';
import { Site } from '@core/models/site.model';
import { HttpClient } from '@angular/common/http';
import { JwtService } from '@core/services/jwt.service';
import { LoaderService } from '@core/services/loader.service';
import { environment } from '@app/../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class SiteService extends BaseHttpService {

  private sitesSubject = new BehaviorSubject<Site[]>([]);
  public sites$ = this.sitesSubject.asObservable();

  private selectedSiteSubject = new BehaviorSubject<Site | null>(null);
  public selectedSite$ = this.selectedSiteSubject.asObservable();

  // Cache for sites
  private sitesCache: Site[] | null = null;
  private sitesLoading: boolean = false;

  private readonly baseApiSpeedPresta = environment.baseApiSpeedpresta;
  private readonly siteUrl = environment.platformSiteUrl;

  constructor(
    http: HttpClient,
    private loaderService: LoaderService,
    jwtService: JwtService
  ) {
    super(jwtService, http);
  }

  /**
   * Load all sites/restaurants
   * @param forceRefresh - Force refresh from API even if cached
   */
  public loadSites(forceRefresh: boolean = false): void {
    try {
      // Return cached data if available and not forcing refresh
      if (!forceRefresh && this.sitesCache) {
        this.sitesSubject.next(this.sitesCache);
        return;
      }

      // Check if already loading to prevent duplicate requests
      if (this.sitesLoading) {
        return;
      }

      // Mark as loading
      this.sitesLoading = true;
      this.loaderService.startLoading();

      const body = {
        status: 'active',
        enterpriseId: this.getUserByToken()?.organization.toLocaleUpperCase(),
        options: { 'options.appId': 'SITES' },
        excluded: {}
      };

      this.retreiveAllFromKey(
        `${this.baseApiSpeedPresta}${this.getUserByToken()?.organization.toLocaleUpperCase()}/${this.siteUrl}`,
        body
      )
        .pipe(
          map(sites =>
            sites.map(site => ({
              identifier: site['options']['identifier'] || site['_id'],
              label: site['options']['label'],
              address: site['options']['adresse'],
              description: site['options']['description'],
            }))
          ),
          finalize(() => {
            this.loaderService.stopLoading();
            this.sitesLoading = false;
          })
        )
        .subscribe({
          next: (sites) => {
            this.sitesCache = sites as Site[];
            this.sitesSubject.next(this.sitesCache);
          },
          error: (error) => {
            console.error('Error loading sites:', error);
            this.sitesLoading = false;
          }
        });
    } catch (error) {
      console.error('Error loading sites:', error);
      this.loaderService.stopLoading();
      this.sitesLoading = false;
    }
  }

  /**
   * Get all sites
   */
  public getSites(): Observable<Site[]> {
    return this.sites$;
  }

  /**
   * Get selected site
   */
  public getSelectedSite(): Observable<Site | null> {
    return this.selectedSite$;
  }

  /**
   * Select a site
   */
  public selectSite(site: Site): void {
    this.selectedSiteSubject.next(site);
    // Store in localStorage for persistence
    localStorage.setItem('selectedSite', JSON.stringify(site));
  }

  /**
   * Clear selected site
   */
  public clearSelectedSite(): void {
    this.selectedSiteSubject.next(null);
    localStorage.removeItem('selectedSite');
  }

  /**
   * Load selected site from localStorage
   */
  public loadSelectedSiteFromStorage(): void {
    const storedSite = localStorage.getItem('selectedSite');
    if (storedSite) {
      try {
        const site = JSON.parse(storedSite) as Site;
        this.selectedSiteSubject.next(site);
      } catch (error) {
        console.error('Error parsing stored site:', error);
        this.clearSelectedSite();
      }
    }
  }

  /**
   * Check if a site is selected
   */
  public hasSiteSelected(): boolean {
    return this.selectedSiteSubject.value !== null;
  }

  /**
   * Get current sites synchronously
   */
  public getCurrentSites(): Site[] {
    return this.sitesSubject.value;
  }

  /**
   * Get currently selected site synchronously
   */
  public getCurrentSelectedSite(): Site | null {
    return this.selectedSiteSubject.value;
  }

  /**
   * Clear sites cache
   * Useful when sites are updated and need to be refreshed
   */
  public clearSitesCache(): void {
    this.sitesCache = null;
    this.sitesLoading = false;
  }
}
